/**
 * 筛选数据加载公共 mixin
 * 统一管理各页面下拉框数据的加载逻辑
 * 注意：不定义 data，由各页面自行定义，避免命名冲突
 */
import request from '../utils/request'

export default {
  data() {
    return {
      dictLookup: {}
    }
  },
  methods: {
    _api() {
      return this.api || request
    },
    /**
     * 加载单个字典类型数据
     * @param {string} type - 字典类型 (grade/subject/semester/exam_type/region/school_year)
     * @param {string} target - 目标变量名
     */
    loadDictType(type, target, stageId) {
      const params = { type }
      if (stageId != null && stageId !== '') {
        params.stageId = stageId
      }
      return this._api().get('/dict', { params }).then(data => {
        let list = data || []
        if (stageId == null && ['exam_type', 'semester', 'school_year'].includes(type)) {
          const seen = new Set()
          list = list.filter(item => {
            if (!item || !item.name || seen.has(item.name)) return false
            seen.add(item.name)
            return true
          })
        }
        this[target] = list
        return list
      })
    },

    /** 按学段加载考试类型、学期、学年；未选学段时加载全部（兼容旧版筛选项） */
    loadStageDictBundle(stageId, targets = {}) {
      const examTypes = targets.examTypes || 'examTypes'
      const semesters = targets.semesters || 'semesters'
      const schoolYears = targets.schoolYears || 'schoolYears'
      const scopedStageId = stageId != null && stageId !== '' ? stageId : null
      return Promise.all([
        this.loadDictType('exam_type', examTypes, scopedStageId),
        this.loadDictType('semester', semesters, scopedStageId),
        this.loadDictType('school_year', schoolYears, scopedStageId)
      ])
    },

    /**
     * 加载年级和学科（最常用的组合）
     * @param {string} gradeTarget - 年级变量名，默认 'grades'
     * @param {string} subjectTarget - 学科变量名，默认 'subjects'
     */
    loadGradeAndSubject(gradeTarget = 'grades', subjectTarget = 'subjects') {
      return Promise.all([
        this.loadDictType('grade', gradeTarget),
        this.loadDictType('subject', subjectTarget)
      ])
    },

    /**
     * 加载试卷列表（从试卷管理模块获取）
     * @param {string} target - 目标变量名，默认 'paperOptions'
     * @param {number} size - 加载数量，默认100
     */
    loadPaperList(target = 'paperOptions', size = 100) {
      const listPath = this.isTeacherMode ? '/papers/list' : '/paper/list'
      return this._api().get(listPath, { params: { page: 0, size } }).then(data => {
        this[target] = (data.content || []).map(p => ({
          id: p.id,
          name: p.title
        }))
        return this[target]
      })
    },

    /**
     * 从 question/filters 接口加载学科列表
     * @param {string} target - 目标变量名，默认 'subjects'
     */
    loadSubjectsFromFilters(target = 'subjects') {
      return this._api().get('/question/filters').then(data => {
        this[target] = data.subjects || []
        return this[target]
      })
    },

    /**
     * 根据字典ID获取名称
     * @param {Array} list - 字典列表
     * @param {number} id - 字典ID
     */
    getDictName(list, id) {
      if (!id || !list) return ''
      const item = list.find(i => i.id === id)
      return item ? item.name : this.getDictDisplayName(id)
    },

    /** 加载完整字典（不去重），用于列表标签按 id 反查名称 */
    loadDictLookup() {
      const types = ['school_year', 'semester', 'exam_type', 'grade', 'subject']
      return Promise.all(
        types.map(type => this._api().get('/dict', { params: { type } }))
      ).then(results => {
        const lookup = {}
        results.flat().forEach(item => {
          if (item && item.id != null) lookup[item.id] = item.name
        })
        this.dictLookup = lookup
        return lookup
      })
    },

    getDictDisplayName(id) {
      if (id == null || id === '') return ''
      return (this.dictLookup && this.dictLookup[id]) || ''
    }
  }
}
